import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  HomeIcon,
  DocumentTextIcon,
  CurrencyDollarIcon,
  CogIcon,
  UsersIcon,
  ChartBarIcon,
  UserIcon,
  Bars3Icon,
  XMarkIcon,
  CubeIcon,
  DocumentIcon,
} from '@heroicons/react/24/outline';

const Layout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { user, logout } = useAuth();
  const location = useLocation();

  // Build navigation by role
  const buildNavigationForRole = (role) => {
    // Admin/Manager: full access
    if (role === 'admin' || role === 'manager') {
      return [
        { name: 'Dashboard', href: '/dashboard', icon: HomeIcon },
        { name: 'Jobs', href: '/jobs', icon: DocumentTextIcon },
        { name: 'Stock', href: '/stock', icon: CubeIcon },
        { name: 'Documents', href: '/documents', icon: DocumentIcon },
        { name: 'Expenses', href: '/expenses', icon: CurrencyDollarIcon },
        { name: 'Services', href: '/services', icon: CogIcon },
        { name: 'Customers', href: '/customers', icon: UsersIcon },
        { name: 'Reports', href: '/reports', icon: ChartBarIcon },
        { name: 'Users', href: '/users', icon: UserIcon },
      ];
    }
    // Sales Manager: sales-focused; no expenses/services/users
    if (role === 'sales_manager') {
      return [
        { name: 'Dashboard', href: '/dashboard', icon: HomeIcon },
        { name: 'Jobs', href: '/jobs', icon: DocumentTextIcon },
        { name: 'Stock', href: '/stock', icon: CubeIcon },
        { name: 'Documents', href: '/documents', icon: DocumentIcon },
        { name: 'Customers', href: '/customers', icon: UsersIcon },
        { name: 'Reports', href: '/reports', icon: ChartBarIcon },
      ];
    }
    // Designer: only own jobs
    if (role === 'designer') {
      return [
        { name: 'Dashboard', href: '/dashboard', icon: HomeIcon },
        { name: 'Jobs', href: '/jobs', icon: DocumentTextIcon },
      ];
    }
    // Operator: production-focused; no reports/expenses/services
    return [
      { name: 'Dashboard', href: '/dashboard', icon: HomeIcon },
      { name: 'Jobs', href: '/jobs', icon: DocumentTextIcon },
    ];
  };

  const navigation = buildNavigationForRole(user?.role);

  const isActive = (href) => {
    if (href === '/dashboard') {
      return location.pathname === '/dashboard';
    }
    return location.pathname.startsWith(href);
  };

  return (
    <div className="h-screen flex overflow-hidden bg-gradient-to-br from-slate-50 via-gray-50 to-zinc-50">
      {/* Mobile sidebar */}
      <div className={`fixed inset-0 flex z-40 md:hidden ${sidebarOpen ? '' : 'hidden'}`}>
        <div className="fixed inset-0 bg-gray-900 bg-opacity-75" onClick={() => setSidebarOpen(false)} />
        <div className="relative flex-1 flex flex-col max-w-xs w-full bg-gradient-to-br from-indigo-900 via-purple-900 to-blue-900">
          <div className="absolute top-0 right-0 -mr-12 pt-2">
            <button
              type="button"
              className="ml-1 flex items-center justify-center h-10 w-10 rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white bg-white bg-opacity-10 backdrop-blur-sm hover:bg-opacity-20 transition-all"
              onClick={() => setSidebarOpen(false)}
            >
              <XMarkIcon className="h-6 w-6 text-white" />
            </button>
          </div>
          <div className="flex-1 h-0 pt-5 pb-4 overflow-y-auto">
            <div className="flex-shrink-0 flex items-center justify-center px-4 mb-8">
              <div className="text-center">
                <h1 className="text-2xl font-bold text-white tracking-wide">Sultan Printing</h1>
                <div className="h-1 w-16 bg-gradient-to-r from-yellow-400 to-yellow-600 rounded-full mx-auto mt-2"></div>
                <p className="text-xs text-indigo-200 mt-2 font-medium">Premium Print Solutions</p>
              </div>
            </div>
            <nav className="mt-5 px-3 space-y-2">
              {navigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`group flex items-center px-4 py-3 text-base font-medium rounded-xl transition-all duration-200 ${
                    isActive(item.href)
                      ? 'bg-white bg-opacity-20 text-white shadow-lg backdrop-blur-sm border border-white border-opacity-20'
                      : 'text-indigo-100 hover:bg-white hover:bg-opacity-10 hover:text-white hover:backdrop-blur-sm'
                  }`}
                  onClick={() => setSidebarOpen(false)}
                >
                  <item.icon
                    className={`mr-4 flex-shrink-0 h-6 w-6 ${
                      isActive(item.href) ? 'text-yellow-300' : 'text-indigo-300 group-hover:text-yellow-300'
                    }`}
                  />
                  {item.name}
                </Link>
              ))}
            </nav>
          </div>
          <div className="flex-shrink-0 border-t border-white border-opacity-20 p-4">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="h-10 w-10 rounded-full bg-gradient-to-br from-yellow-400 to-yellow-600 flex items-center justify-center text-white font-bold text-lg shadow-lg">
                  {user?.name?.charAt(0).toUpperCase()}
                </div>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-white">{user?.name}</p>
                <p className="text-xs text-indigo-200 capitalize">{user?.role?.replace('_', ' ')}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Desktop sidebar */}
      <div className="hidden md:flex md:flex-shrink-0">
        <div className="flex flex-col w-64">
          <div className="flex flex-col h-0 flex-1 bg-gradient-to-br from-indigo-900 via-purple-900 to-blue-900 shadow-2xl">
            <div className="flex-1 flex flex-col pt-5 pb-4 overflow-y-auto">
              <div className="flex items-center justify-center flex-shrink-0 px-4 mb-8">
                <div className="text-center">
                  <h1 className="text-2xl font-bold text-white tracking-wide">Sultan Printing</h1>
                  <div className="h-1 w-16 bg-gradient-to-r from-yellow-400 to-yellow-600 rounded-full mx-auto mt-2"></div>
                  <p className="text-xs text-indigo-200 mt-2 font-medium">Premium Print Solutions</p>
                </div>
              </div>
              <nav className="mt-5 flex-1 px-3 space-y-2">
                {navigation.map((item) => (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`group flex items-center px-4 py-3 text-sm font-medium rounded-xl transition-all duration-200 ${
                      isActive(item.href)
                        ? 'bg-white bg-opacity-20 text-white shadow-lg backdrop-blur-sm border border-white border-opacity-20'
                        : 'text-indigo-100 hover:bg-white hover:bg-opacity-10 hover:text-white hover:backdrop-blur-sm'
                    }`}
                  >
                    <item.icon
                      className={`mr-3 flex-shrink-0 h-6 w-6 ${
                        isActive(item.href) ? 'text-yellow-300' : 'text-indigo-300 group-hover:text-yellow-300'
                      }`}
                    />
                    {item.name}
                  </Link>
                ))}
              </nav>
            </div>
            <div className="flex-shrink-0 border-t border-white border-opacity-20 p-4">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="h-10 w-10 rounded-full bg-gradient-to-br from-yellow-400 to-yellow-600 flex items-center justify-center text-white font-bold text-lg shadow-lg">
                    {user?.name?.charAt(0).toUpperCase()}
                  </div>
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-white">{user?.name}</p>
                  <p className="text-xs text-indigo-200 capitalize">{user?.role?.replace('_', ' ')}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="flex flex-col w-0 flex-1 overflow-hidden">
        <div className="md:hidden pl-1 pt-1 sm:pl-3 sm:pt-3">
          <button
            type="button"
            className="-ml-0.5 -mt-0.5 h-12 w-12 inline-flex items-center justify-center rounded-md text-gray-500 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500"
            onClick={() => setSidebarOpen(true)}
          >
            <Bars3Icon className="h-6 w-6" />
          </button>
        </div>
        <main className="flex-1 relative z-0 overflow-y-auto focus:outline-none">
          <div className="py-6">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8">
              {/* Header */}
              <div className="flex justify-between items-center mb-6 bg-white rounded-2xl shadow-lg p-6 border border-indigo-100">
                <div>
                  <h1 className="text-3xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                    {navigation.find(item => isActive(item.href))?.name || 'Dashboard'}
                  </h1>
                  <p className="text-sm text-gray-500 mt-1">Manage your printing operations efficiently</p>
                </div>
                <div className="flex items-center space-x-4">
                  <div className="text-right">
                    <p className="text-xs text-gray-400">Welcome back,</p>
                    <p className="text-sm font-semibold text-gray-700">{user?.name}</p>
                  </div>
                  <Link
                    to="/profile"
                    className="h-10 w-10 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white font-bold shadow-lg hover:opacity-90"
                    title="Profile"
                  >
                    {user?.name?.charAt(0).toUpperCase()}
                  </Link>
                  <div className="relative">
                    <button
                      onClick={logout}
                      className="px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-indigo-600 to-purple-600 rounded-lg hover:from-indigo-700 hover:to-purple-700 transition-all shadow-md hover:shadow-lg"
                    >
                      Logout
                    </button>
                  </div>
                </div>
              </div>
              
              {/* Page content */}
              {children}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default Layout; 